import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "section", "trigger", "content", "chevron", "checkbox",
    "modalOverlay", "modalTitle", "modalBody",
    "categoryGrid", "tagGrid", "collectionGrid",
    "categoryCount", "tagCount", "collectionCount"
  ]
  static values = {
    categoryUrl: String,
    tagUrl: String,
    collectionUrl: String
  }

  connect() {
    this.sectionTargets.forEach((section) => {
      const trigger = section.querySelector('[data-organization-panel-target="trigger"]')
      const content = section.querySelector('[data-organization-panel-target="content"]')
      const isExpanded = trigger?.getAttribute("aria-expanded") === "true"
      if (content && isExpanded) {
        content.style.maxHeight = content.scrollHeight + "px"
      }
    })
    this.updateAllCounts()

    // Handle escape key to close modal
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.boundHandleKeydown)
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundHandleKeydown)
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.hasModalOverlayTarget && !this.modalOverlayTarget.classList.contains('hidden')) {
      this.closeModal()
    }
  }

  toggle(event) {
    const trigger = event.currentTarget
    const section = trigger.closest('[data-organization-panel-target="section"]')
    const content = section.querySelector('[data-organization-panel-target="content"]')
    const chevron = section.querySelector('[data-organization-panel-target="chevron"]')

    const isExpanded = trigger.getAttribute("aria-expanded") === "true"

    if (isExpanded) {
      content.style.maxHeight = content.scrollHeight + "px"
      content.offsetHeight
      content.style.maxHeight = "0"
      trigger.setAttribute("aria-expanded", "false")
      chevron.style.transform = "rotate(-90deg)"
      section.classList.add("org-section--collapsed")
    } else {
      content.style.maxHeight = content.scrollHeight + "px"
      trigger.setAttribute("aria-expanded", "true")
      chevron.style.transform = "rotate(0deg)"
      section.classList.remove("org-section--collapsed")

      setTimeout(() => {
        if (trigger.getAttribute("aria-expanded") === "true") {
          content.style.maxHeight = "none"
        }
      }, 300)
    }
  }

  checkboxTargetConnected(checkbox) {
    checkbox.addEventListener("change", () => this.updateAllCounts())
  }

  updateAllCounts() {
    this.sectionTargets.forEach(section => {
      const checkboxes = section.querySelectorAll('[data-organization-panel-target="checkbox"]')
      const countEl = section.querySelector('.org-section-count')
      if (countEl && checkboxes.length > 0) {
        const checked = Array.from(checkboxes).filter(cb => cb.checked).length
        const total = checkboxes.length
        countEl.textContent = `${checked} of ${total} selected`

        const header = section.querySelector('.org-section-header')
        if (checked > 0) {
          header.classList.add('org-section-header--has-selection')
        } else {
          header.classList.remove('org-section-header--has-selection')
        }
      }
    })
  }

  // Modal methods
  openCategoryModal(event) {
    event.preventDefault()
    this.currentModalType = 'category'
    this.modalTitleTarget.textContent = 'Add Category'
    this.modalBodyTarget.innerHTML = this.categoryFormHTML()
    this.showModal()
    this.modalBodyTarget.querySelector('input[name="category[name]"]')?.focus()
  }

  openTagModal(event) {
    event.preventDefault()
    this.currentModalType = 'tag'
    this.modalTitleTarget.textContent = 'Add Tag'
    this.modalBodyTarget.innerHTML = this.tagFormHTML()
    this.showModal()
    this.modalBodyTarget.querySelector('input[name="tag[name]"]')?.focus()
  }

  openCollectionModal(event) {
    event.preventDefault()
    this.currentModalType = 'collection'
    this.modalTitleTarget.textContent = 'Add Collection'
    this.modalBodyTarget.innerHTML = this.collectionFormHTML()
    this.showModal()
    this.modalBodyTarget.querySelector('input[name="collection[name]"]')?.focus()
  }

  showModal() {
    this.modalOverlayTarget.classList.remove('hidden')
    document.body.style.overflow = 'hidden'

    // Bind events for dynamically injected content
    setTimeout(() => {
      const cancelBtn = this.modalBodyTarget.querySelector('.btn-secondary')
      if (cancelBtn) {
        cancelBtn.addEventListener('click', (e) => {
          e.preventDefault()
          this.closeModal()
        })
      }

      const form = this.modalBodyTarget.querySelector('form')
      if (form) {
        form.addEventListener('submit', (e) => this.submitForm(e))
      }
    }, 0)
  }

  closeModal() {
    this.modalOverlayTarget.classList.add('hidden')
    document.body.style.overflow = ''
    this.currentModalType = null
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  async submitForm(event) {
    event.preventDefault()
    const form = event.currentTarget
    const submitBtn = form.querySelector('button[type="submit"]')
    const originalText = submitBtn.textContent

    submitBtn.disabled = true
    submitBtn.textContent = 'Creating...'

    try {
      const formData = new FormData(form)
      let url

      switch (this.currentModalType) {
        case 'category':
          url = this.categoryUrlValue
          break
        case 'tag':
          url = this.tagUrlValue
          break
        case 'collection':
          url = this.collectionUrlValue
          break
      }

      const response = await fetch(url, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      if (response.ok) {
        const data = await response.json()
        this.addNewItem(data)
        this.closeModal()
      } else {
        const errorData = await response.json()
        this.showFormError(errorData.errors || ['Something went wrong'])
        submitBtn.disabled = false
        submitBtn.textContent = originalText
      }
    } catch (error) {
      console.error('Error creating item:', error)
      this.showFormError(['Network error. Please try again.'])
      submitBtn.disabled = false
      submitBtn.textContent = originalText
    }
  }

  showFormError(errors) {
    const errorEl = this.modalBodyTarget.querySelector('.org-modal-error')
    if (errorEl) {
      errorEl.textContent = errors.join(', ')
      errorEl.classList.remove('hidden')
    }
  }

  addNewItem(data) {
    switch (this.currentModalType) {
      case 'category':
        this.addCategoryChip(data)
        break
      case 'tag':
        this.addTagChip(data)
        break
      case 'collection':
        this.addCollectionCard(data)
        break
    }
  }

  addCategoryChip(category) {
    const grid = this.categoryGridTarget
    if (!grid) return

    grid.classList.remove('hidden')
    const formName = this.getFormFieldName()

    const chip = document.createElement('label')
    chip.className = 'org-category-chip group'
    chip.innerHTML = `
      <input type="checkbox" name="${formName}[category_ids][]" value="${category.id}" class="sr-only peer" data-organization-panel-target="checkbox" checked>
      <div class="org-category-chip-inner">
        ${category.color ? `<span class="org-category-dot" style="background-color: ${category.color}"></span>` : ''}
        <span class="org-category-name">${this.escapeHtml(category.name)}</span>
        <svg class="org-category-check" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
        </svg>
      </div>
    `
    grid.appendChild(chip)

    const checkbox = chip.querySelector('input')
    checkbox.addEventListener('change', () => this.updateAllCounts())
    this.updateAllCounts()
  }

  addTagChip(tag) {
    const grid = this.tagGridTarget
    if (!grid) return

    grid.classList.remove('hidden')
    const formName = this.getFormFieldName()

    const chip = document.createElement('label')
    chip.className = 'org-tag group'
    const colorStyle = tag.color ? `--tag-color: ${tag.color}; --tag-color-light: ${tag.color}20` : ''
    chip.innerHTML = `
      <input type="checkbox" name="${formName}[tag_ids][]" value="${tag.id}" class="sr-only peer" data-organization-panel-target="checkbox" checked>
      <span class="org-tag-inner" style="${colorStyle}">
        ${tag.color ? `<span class="org-tag-dot" style="background-color: ${tag.color}"></span>` : ''}
        ${this.escapeHtml(tag.name)}
      </span>
    `
    grid.appendChild(chip)

    const checkbox = chip.querySelector('input')
    checkbox.addEventListener('change', () => this.updateAllCounts())
    this.updateAllCounts()
  }

  addCollectionCard(collection) {
    const grid = this.collectionGridTarget
    if (!grid) return

    grid.classList.remove('hidden')
    const formName = this.getFormFieldName()

    const card = document.createElement('label')
    card.className = 'org-collection-card group'
    card.innerHTML = `
      <input type="checkbox" name="${formName}[collection_ids][]" value="${collection.id}" class="sr-only peer" data-organization-panel-target="checkbox" checked>
      <div class="org-collection-card-inner">
        <div class="org-collection-visual">
          <div class="org-collection-placeholder">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <div class="org-collection-check">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
          </div>
        </div>
        <div class="org-collection-info">
          <span class="org-collection-name">${this.escapeHtml(collection.name)}</span>
          <span class="org-collection-meta">0 books</span>
        </div>
      </div>
    `
    grid.appendChild(card)

    const checkbox = card.querySelector('input')
    checkbox.addEventListener('change', () => this.updateAllCounts())
    this.updateAllCounts()
  }

  getFormFieldName() {
    const form = this.element.closest('form')
    if (!form) return 'item'

    const firstInput = form.querySelector('input[name*="["]')
    if (firstInput) {
      const match = firstInput.name.match(/^([^\[]+)/)
      return match ? match[1] : 'item'
    }
    return 'item'
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  categoryFormHTML() {
    return `
      <form class="org-modal-form">
        <div class="org-modal-error hidden"></div>
        <div class="org-modal-field">
          <label class="form-label">Name</label>
          <input type="text" name="category[name]" class="form-input" required placeholder="e.g., Fiction, Non-Fiction">
        </div>
        <div class="org-modal-field">
          <label class="form-label">Color (optional)</label>
          <div class="org-color-picker">
            <input type="color" name="category[color]" value="#d97706" class="org-color-input">
            <input type="text" class="form-input org-color-text" placeholder="#d97706">
          </div>
        </div>
        <div class="org-modal-actions">
          <button type="button" class="btn-secondary">Cancel</button>
          <button type="submit" class="btn-primary">Create Category</button>
        </div>
      </form>
    `
  }

  tagFormHTML() {
    return `
      <form class="org-modal-form">
        <div class="org-modal-error hidden"></div>
        <div class="org-modal-field">
          <label class="form-label">Name</label>
          <input type="text" name="tag[name]" class="form-input" required placeholder="e.g., Favorites, To Read">
        </div>
        <div class="org-modal-field">
          <label class="form-label">Color (optional)</label>
          <div class="org-color-picker">
            <input type="color" name="tag[color]" value="#7c3aed" class="org-color-input">
            <input type="text" class="form-input org-color-text" placeholder="#7c3aed">
          </div>
        </div>
        <div class="org-modal-actions">
          <button type="button" class="btn-secondary">Cancel</button>
          <button type="submit" class="btn-primary">Create Tag</button>
        </div>
      </form>
    `
  }

  collectionFormHTML() {
    return `
      <form class="org-modal-form">
        <div class="org-modal-error hidden"></div>
        <div class="org-modal-field">
          <label class="form-label">Name</label>
          <input type="text" name="collection[name]" class="form-input" required placeholder="e.g., Summer Reading, Classics">
        </div>
        <div class="org-modal-field">
          <label class="form-label">Description (optional)</label>
          <textarea name="collection[description]" class="form-textarea" rows="2" placeholder="What's this collection about?"></textarea>
        </div>
        <div class="org-modal-actions">
          <button type="button" class="btn-secondary">Cancel</button>
          <button type="submit" class="btn-primary">Create Collection</button>
        </div>
      </form>
    `
  }

}
